import React, { useEffect, useMemo, useRef, useState } from 'react';
import { tone, displayRating, overallHeadline } from '../utils/csiReport';
import { useTheme } from '../hooks/useTheme';

/**
 * CSICelebration: the score-reveal shown right after the assessment is submitted.
 * A big overall-score donut counts up from 0 to the real score, its color
 * sweeping grey -> red -> amber -> green as it climbs; then the rating pill and
 * the coaching headline/message appear, with a Continue button (auto-advances
 * after ~10s). On continue the donut (with its pill) and the commentary fly into
 * their real positions on the result page -- the report is rendered underneath
 * this overlay, and we FLIP into its [data-flip="donut"] / [data-flip="commentary"]
 * anchors. If the anchors aren't found it just fades out (no hard dependency).
 * Respects prefers-reduced-motion.
 *
 * Props: result {overall, overallPercent, ...}, onDone().
 */

const R = 80;
const C = 2 * Math.PI * R;
// Arc color sweep: grey (0) -> red -> amber -> green as the value climbs.
const STOPS = [[0, [203, 213, 225]], [25, [239, 68, 68]], [55, [234, 179, 8]], [80, [34, 197, 94]], [100, [22, 163, 74]]];
function lerpColor(v) {
    for (let i = 0; i < STOPS.length - 1; i++) {
        const [a, ca] = STOPS[i], [b, cb] = STOPS[i + 1];
        if (v <= b) { const t = (v - a) / (b - a); return `rgb(${ca.map((c, j) => Math.round(c + (cb[j] - c) * t)).join(',')})`; }
    }
    return `rgb(${STOPS[STOPS.length - 1][1].join(',')})`;
}
const CONF = ['#10b981', '#eab308', '#ef4444', '#3b82f6', '#a855f7'];
// "green" is the positive rating band — kept as the semantic success color
// (not brand primary blue), since "blue" already means a distinct rating
// here ("Needs More Information").
const PILL_LIGHT = { green: 'bg-success-tint text-success', yellow: 'bg-amber-100 text-amber-800', red: 'bg-rose-100 text-rose-800', blue: 'bg-blue-100 text-blue-800' };
const PILL_DARK = { green: 'bg-success/20 text-success', yellow: 'bg-amber-900/40 text-amber-300', red: 'bg-rose-900/40 text-rose-300', blue: 'bg-blue-900/40 text-blue-300' };

export default function CSICelebration({ result, onDone }) {
    const { isDarkMode } = useTheme();
    const overall = result?.overall || {};
    // No overall score yet ("more data needed") -> show a dash, not 0, so it
    // matches the report and the hand-off doesn't flip — <-> 0.
    const hasScore = result?.overallPercent != null;
    const target = hasScore ? Math.max(0, Math.min(100, Math.round(result.overallPercent))) : 0;
    const reduced = typeof window !== 'undefined' && window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const [phase, setPhase] = useState('count'); // 'count' | 'scored' | 'done'
    const arcRef = useRef(null), numRef = useRef(null), donutRef = useRef(null), commentRef = useRef(null);
    const doneCalled = useRef(false);

    const paint = (v) => {
        if (arcRef.current) {
            arcRef.current.style.strokeDasharray = `${C}`;
            arcRef.current.style.strokeDashoffset = `${C * (1 - v / 100)}`;
            arcRef.current.style.stroke = lerpColor(v);
        }
        if (numRef.current) numRef.current.textContent = Math.round(v);
    };

    // Count-up
    useEffect(() => {
        if (!hasScore) {
            // Nothing to count to — show the dash and an empty arc, then reveal
            // the commentary/continue (same as the report's "—" state).
            if (numRef.current) numRef.current.textContent = '—';
            if (arcRef.current) arcRef.current.style.strokeDasharray = `0 ${C}`;
            setPhase('scored');
            return;
        }
        if (reduced) { paint(target); setPhase('scored'); return; }
        let raf; const dur = 2200;
        const start = setTimeout(() => {
            const t0 = performance.now();
            const frame = (now) => {
                const p = Math.min(1, (now - t0) / dur), e = 1 - Math.pow(1 - p, 3);
                paint(target * e);
                if (p < 1) raf = requestAnimationFrame(frame); else setPhase('scored');
            };
            raf = requestAnimationFrame(frame);
        }, 450);
        return () => { clearTimeout(start); cancelAnimationFrame(raf); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Auto-advance ~10s after the score is revealed.
    useEffect(() => {
        if (phase !== 'scored' || reduced) return;
        const t = setTimeout(goContinue, 10000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase]);

    const finish = () => { if (doneCalled.current) return; doneCalled.current = true; onDone?.(); };

    function goContinue() {
        setPhase(prev => (prev === 'done' ? prev : 'done'));
        try {
            window.scrollTo(0, 0);
            const flip = (el, sel, scaleToo) => {
                const tgt = document.querySelector(sel);
                if (!el || !tgt) return;
                const f = el.getBoundingClientRect(), t = tgt.getBoundingClientRect();
                if (!f.width || !t.width) return;
                const s = scaleToo ? (t.width / f.width) : 1;
                el.style.transformOrigin = 'top center';
                el.style.transform = `translate(${(t.left + t.width / 2) - (f.left + f.width / 2)}px, ${t.top - f.top}px)${scaleToo ? ` scale(${s})` : ''}`;
            };
            flip(donutRef.current, '[data-flip="donut"]', true);
            flip(commentRef.current, '[data-flip="commentary"]', false);
        } catch { /* no anchors -> just fade */ }
        setTimeout(finish, reduced ? 0 : 850);
    }

    const pieces = useMemo(() => reduced ? [] : Array.from({ length: 44 }, (_, i) => ({
        id: i, left: 50 + (Math.random() * 36 - 18), color: CONF[i % CONF.length],
        dx: (Math.random() * 2 - 1) * 260, dy: (Math.random() * -1 - 0.4) * 220, rot: Math.random() * 720 - 360,
    })), [reduced]);

    const heading = isDarkMode ? 'text-slate-100' : 'text-slate-900';
    const sub = isDarkMode ? 'text-slate-400' : 'text-slate-600';
    const pageBg = isDarkMode ? 'bg-surface' : 'bg-surface';
    const pill = (isDarkMode ? PILL_DARK : PILL_LIGHT)[tone(overall.rating)];

    return (
        <div className={`fixed inset-0 z-[200] flex flex-col items-center justify-center overflow-hidden transition-colors duration-500 ${phase === 'done' ? 'bg-transparent pointer-events-none' : pageBg}`}>
            <style>{`@keyframes csiconf{0%{transform:translate(0,0);opacity:1}70%{transform:translate(var(--dx),var(--dy)) rotate(var(--rot));opacity:1}100%{transform:translate(calc(var(--dx) * 1.4), calc(var(--dy) + 560px)) rotate(var(--rot));opacity:0}}`}</style>

            {/* confetti */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
                {pieces.map(p => (
                    <span key={p.id} style={{ position: 'absolute', left: `${p.left}%`, top: '34%', width: 9, height: 9, borderRadius: 2, background: p.color, animation: 'csiconf 1.9s cubic-bezier(.2,.6,.3,1) forwards', '--dx': `${p.dx}px`, '--dy': `${p.dy}px`, '--rot': `${p.rot}deg` }} />
                ))}
            </div>

            <h1 className={`text-2xl font-extrabold mb-3 transition-opacity duration-500 ${phase === 'done' ? 'opacity-0' : 'opacity-100'} ${heading}`}>Congratulations! 🎉</h1>

            {/* donut group (dial + pill) — flies into the report on continue */}
            <div ref={donutRef} className="flex flex-col items-center gap-2 transition-transform duration-[850ms] ease-[cubic-bezier(.22,1,.36,1)]">
                <div className="relative" style={{ width: 150, height: 150 }}>
                    <svg width="150" height="150" viewBox="0 0 200 200">
                        <circle cx="100" cy="100" r="80" fill="none" stroke={isDarkMode ? '#334155' : '#eceae3'} strokeWidth="14" />
                        <circle ref={arcRef} cx="100" cy="100" r="80" fill="none" stroke="#cbd5e1" strokeWidth="14" strokeLinecap="round" transform="rotate(-90 100 100)" />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span className={`text-4xl font-extrabold leading-none ${heading}`}>
                            <span ref={numRef}>{hasScore ? '0' : '—'}</span><span className={`text-lg font-semibold ${sub}`}>/100</span>
                        </span>
                    </div>
                </div>
                {overall.rating && (
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full transition-opacity duration-300 ${phase === 'count' ? 'opacity-0' : 'opacity-100'} ${pill}`}>
                        {displayRating(overall.rating)}
                    </span>
                )}
            </div>

            {/* commentary — flies too */}
            <div ref={commentRef} className={`mt-3 text-center px-6 max-w-md transition-[opacity,transform] duration-[850ms] ease-[cubic-bezier(.22,1,.36,1)] ${phase === 'count' ? 'opacity-0 translate-y-2' : 'opacity-100'}`}>
                <p className={`text-lg font-bold ${heading}`}>{overallHeadline(overall.rating)}</p>
                {overall.coachingMessage && <p className={`text-sm mt-1 ${sub}`}>{overall.coachingMessage}</p>}
            </div>

            {/* continue + auto-advance countdown */}
            <div className={`mt-5 flex flex-col items-center gap-2 transition-opacity duration-300 ${phase === 'scored' ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                <button onClick={goContinue} className="rounded-full bg-primary-600 px-6 py-2.5 font-semibold text-white shadow-lg shadow-primary-600/30 hover:bg-primary-700 transition-colors">
                    Continue
                </button>
                <div className="h-1 w-36 rounded-full overflow-hidden bg-slate-300/60">
                    <div className={`h-full rounded-full bg-slate-400 ${phase === 'scored' && !reduced ? 'w-0' : 'w-full'}`}
                        style={{ transition: phase === 'scored' && !reduced ? 'width 10s linear' : 'none' }} />
                </div>
            </div>
        </div>
    );
}
